import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import {
    Box, Button, Card, Chip, CardActions, CardContent,
    Container, Divider, Grid, Typography, CircularProgress
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLIC_KEY
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
    : null;

const tiers = [
    {
        title: 'Free',
        plan: 'free',
        price: '£0',
        subtext: 'Trial and Students',
        description: [
            'Up to 5 projects',
            'No AI Machine Translation',
            'Support for top 10 languages',
            'Standard File Support',
            'No Glossary or TM',
            'App integrations (PAYG)',
            'Solo use only',
            'Email support',
        ],
        buttonVariant: 'outlined',
        buttonColor: 'primary',
    },
    {
        title: 'Essential',
        plan: 'essential',
        price: '£79',
        subtext: 'For solo freelancers',
        description: [
            'Up to 20 projects per month',
            '100,000 AI characters per month',
            'Support for 25 languages',
            'Broad File Support (DOCX, PDF, etc)',
            'Standard Glossary & TM',
            '1 App integration',
            'Solo use only',
            'Email support',
        ],
        buttonVariant: 'contained',
        buttonColor: 'primary',
    },
    {
        title: 'Growth',
        plan: 'growth',
        subheader: 'Most Popular',
        price: '£249',
        subtext: 'For small teams and agencies',
        description: [
            'Up to 200 projects per month',
            '1,000,000 AI characters',
            '100+ languages supported',
            'Fuzzy Match TM & Bulk Glossary',
            'Invite up to 10 team members',
            'Team chat and project assignment',
            '5 App integrations included',
            'IDML Support & Batch Processing',
            'Premium support',
        ],
        buttonVariant: 'contained',
        buttonColor: 'secondary',
    },
    {
        title: 'Scale',
        plan: 'scale',
        price: 'Contact Us',
        subtext: 'For large businesses',
        description: [
            'Unlimited projects and words',
            'Unlimited AI usage',
            'Full language support',
            'Unlimited app integrations',
            'Custom team size and permissions',
            'Custom security, SSO, & API',
            'Dedicated account manager',
            'Custom contracts & SLAs',
            'Phone, email, & premium support',
        ],
        buttonVariant: 'outlined',
        buttonColor: 'primary',
    },
];

export default function Pricing() {
    const token = useSelector((state) => state.auth.token);
    const [loadingPlan, setLoadingPlan] = React.useState(null);

    const handleCheckout = async (planKey) => {
        if (!token) return;
        setLoadingPlan(planKey);
        try {
            const dashboardUrl = `${window.location.origin}/#/dashboard`;
            const successUrl = `${dashboardUrl}?checkoutSuccess=true&session_id={CHECKOUT_SESSION_ID}`;
            const cancelUrl = `${dashboardUrl}?checkoutCanceled=true`;

            const { data } = await axios.post('/api/billing/checkout',
                { planKey, successUrl, cancelUrl },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (stripePromise && data.id) {
                const stripe = await stripePromise;
                const { error } = await stripe.redirectToCheckout({ sessionId: data.id });
                if (error) throw error;
            } else if (data.url) {
                window.location.href = data.url;
            }
        } catch (err) {
            console.error('Checkout error:', err);
            alert('Unable to start checkout. Please try again.');
        } finally {
            setLoadingPlan(null);
        }
    };

    return (
        <Container
            id="pricing"
            sx={{
                pt: { xs: 4, sm: 12 },
                pb: { xs: 8, sm: 16 },
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: { xs: 3, sm: 6 },
            }}
        >
            <Box
                sx={{
                    width: { sm: '100%', md: '60%' },
                    textAlign: { sm: 'left', md: 'center' },
                }}
            >
                <Typography
                    component="h2"
                    variant="h4"
                    gutterBottom
                    sx={{ color: 'text.primary' }}
                >
                    Pricing
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Choose the right plan for your workflow. <b>Essential</b> is ideal for solo freelancers, <b>Growth</b> empowers small teams, and <b>Scale</b> unlocks advanced features for large businesses.
                </Typography>
            </Box>
            <Grid
                container
                spacing={3}
                sx={{ alignItems: 'center', justifyContent: 'center', width: '100%' }}
            >
                {tiers.filter(t => !token || t.plan !== 'free').map((tier) => (
                    <Grid
                        item
                        xs={12}
                        sm={tier.title === 'Scale' ? 12 : 6}
                        md={4}
                        key={tier.title}
                    >
                        <Card
                            sx={[
                                {
                                    p: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4,
                                },
                                tier.title === 'Growth' &&
                                ((theme) => ({
                                    border: 'none',
                                    background:
                                        'radial-gradient(circle at 50% 0%, hsl(220, 20%, 35%), hsl(220, 30%, 6%))',
                                    boxShadow: `0 8px 12px hsla(220, 20%, 42%, 0.2)`,
                                    ...theme.applyStyles?.('dark', {
                                        background:
                                            'radial-gradient(circle at 50% 0%, hsl(220, 20%, 20%), hsl(220, 30%, 16%))',
                                        boxShadow: `0 8px 12px hsla(0, 0%, 0%, 0.8)`,
                                    }),
                                })),
                            ]}
                        >
                            <CardContent>
                                <Box
                                    sx={[
                                        {
                                            mb: 1,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: 2,
                                        },
                                        tier.title === 'Growth'
                                            ? { color: 'grey.100' }
                                            : { color: '' },
                                    ]}
                                >
                                    <Box>
                                        <Typography component="h3" variant="h6">
                                            {tier.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {tier.subtext}
                                        </Typography>
                                    </Box>
                                    {tier.title === 'Growth' && (
                                        <Chip icon={<AutoAwesomeIcon />} label={tier.subheader} />
                                    )}
                                </Box>
                                <Box
                                    sx={[
                                        {
                                            display: 'flex',
                                            alignItems: 'baseline',
                                        },
                                        tier.title === 'Growth'
                                            ? { color: 'grey.50' }
                                            : { color: null },
                                    ]}
                                >
                                    <Typography component="h3" variant="h2">
                                        {tier.price}
                                        {tier.price !== 'Contact Us' && (
                                            <Typography component="span" variant="h6" sx={{ color: 'text.secondary', ml: 1 }}>
                                                / month
                                            </Typography>
                                        )}
                                    </Typography>
                                </Box>
                                <Divider sx={{ my: 2, opacity: 0.8, borderColor: 'divider' }} />
                                {tier.description.map((line) => (
                                    <Box
                                        key={line}
                                        sx={{ py: 1, display: 'flex', gap: 1.5, alignItems: 'center' }}
                                    >
                                        <CheckCircleRoundedIcon
                                            sx={[
                                                {
                                                    width: 20,
                                                },
                                                tier.title === 'Growth'
                                                    ? { color: 'primary.light' }
                                                    : { color: 'primary.main' },
                                            ]}
                                        />
                                        <Typography
                                            variant="subtitle2"
                                            component={'span'}
                                            sx={[
                                                tier.title === 'Growth'
                                                    ? { color: 'grey.50' }
                                                    : { color: null },
                                            ]}
                                        >
                                            {line}
                                        </Typography>
                                    </Box>
                                ))}
                            </CardContent>
                            <CardActions>
                                <Button
                                    fullWidth
                                    variant={tier.buttonVariant}
                                    color={tier.buttonColor}
                                    disabled={loadingPlan === tier.plan}
                                    component={(!token || tier.plan === 'scale' || tier.plan === 'free') ? RouterLink : 'button'}
                                    to={
                                        !token
                                            ? (tier.plan === 'scale' ? '/contact' : `/register?plan=${tier.plan}`)
                                            : (tier.plan === 'scale' ? '/contact' : (tier.plan === 'free' ? '/dashboard' : undefined))
                                    }
                                    onClick={token && tier.plan !== 'scale' && tier.plan !== 'free' ? () => handleCheckout(tier.plan) : undefined}
                                >
                                    {loadingPlan === tier.plan ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        tier.plan === 'scale' ? 'Contact Sales' : (token ? (tier.plan === 'free' ? 'Go to Dashboard' : 'Upgrade Now') : 'Get Started')
                                    )}
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
}
